import React, { useState } from 'react';

import { getFileVersionDisplayName, toDocParts } from '@aline/common';
import type { MenuProps } from '@mui/material';
import { Chip, Divider, Menu, MenuItem } from '@mui/material';
import { Download, FileJson, Trash2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { keys } from 'ramda';
import { AlineTooltip, ConfirmationDialog } from 'src/components';
import {
  useDownloadJSONWithLoading,
  useInitiateDocxExport,
  useIsJsonDownloadAvailable,
} from 'src/editor';
import { Apollo } from 'src/gql/generated';
import { useVersionHistoryParams } from 'src/helpers';
import { useLogAndDispatchErrorSnack } from 'src/helpers/snackbars';
import {
  useDeleteFileVersion,
  useDownloadFileVersionWithLoading,
  useGetDocumentVersions,
  useGoToLatestVersion,
} from 'src/hooks';
import { CrmIntegrationIcon } from 'src/images/integrations/CrmIntegrationIcon';
import { themeV5 } from 'src/theme';
import type { ReactSetStateAction } from 'src/types';
import { getWorkflowWasSentForSig } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';

import type { VersionCardVersion } from './VersionCard';
import type { MonthDay } from './VersionCardList';

interface VersionCardMenuProps extends MenuProps {
  canEdit: boolean;
  isLatestVersion: boolean;
  setMonthDay: ReactSetStateAction<MonthDay | null>;
  setOpen: ReactSetStateAction<boolean>;
  version: VersionCardVersion;
  disableDelete?: boolean;
}

interface VersionSyncCrmConfig {
  enabled: boolean;
  label: string;
  record: string;
}

const VERSION_SYNC_CRM = {
  [Apollo.Integration.SalesForce]: { enabled: true, label: 'Salesforce', record: 'opportunity' },
} as const satisfies Partial<Record<Apollo.Integration, VersionSyncCrmConfig>>;

type VersionSyncCrm = keyof typeof VERSION_SYNC_CRM;

const VERSION_SYNC_CRMS: VersionSyncCrm[] = keys(VERSION_SYNC_CRM);

const VERSION_SYNC_DISABLED_REASON = {
  comingSoon: 'Coming soon',
  noEditAccess: 'You need edit access to sync',
  noLinkedRecord: (crm: VersionSyncCrmConfig) => `Link a ${crm.label} ${crm.record} to sync`,
  syncing: 'Sync already in progress',
} as const;

const useStyles = makeStyles()(theme => ({
  crmIcon: {
    display: 'block',
    height: 16,
    marginRight: 8,
    objectFit: 'contain',
    width: 16,
  },
  menuItem: {
    '&&': {
      backgroundColor: 'transparent',
    },
    '&&:hover, &&.Mui-focusVisible, &&.Mui-selected': {
      backgroundColor: 'transparent',
    },
  },
  menuRow: {
    '&:hover': {
      backgroundColor: theme.appColors.hoverBackground,
    },
    display: 'block',
    width: '100%',
  },
  sectionDivider: {
    margin: theme.spacing(0.5, 0),
  },
}));

/** Version row kebab: download, delete, and CRM version sync on workflow documents. */
export function VersionCardMenu({
  canEdit,
  disableDelete,
  isLatestVersion,
  setMonthDay,
  setOpen,
  version,
  ...rest
}: VersionCardMenuProps): JSX.Element {
  const { deleteFileVersion } = useDeleteFileVersion();
  const { goToLatestVersion } = useGoToLatestVersion();
  const versionName = getFileVersionDisplayName(version);
  const { documentId, versionId } = useVersionHistoryParams();
  const { document } = useGetDocumentVersions(documentId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const workflowId = document?.workflow?.id ?? null;
  const { data: linkedOpportunitiesData } = Apollo.useGetWorkflowLinkedOpportunitiesQuery({
    skip: !workflowId,
    variables: { workflowId: workflowId ?? '' },
  });
  const { enqueueSnackbar } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [syncCrm, setSyncCrm] = useState<VersionSyncCrm | null>(null);
  const [syncFileVersionToCrm, { loading: syncingToCrm }] =
    Apollo.useSyncFileVersionToCrmMutation();
  const syncCrmConfig = syncCrm ? VERSION_SYNC_CRM[syncCrm] : null;
  const { initiateDocxExport } = useInitiateDocxExport();
  const { downloadFileVersionWithLoading } = useDownloadFileVersionWithLoading();
  const { downloadJSONWithLoading } = useDownloadJSONWithLoading();
  const isNonSignedAlineDocumentVersion =
    version.__typename === 'DocumentVersion' && !document?.isSigned;
  const docParts =
    version.__typename === 'DocumentVersion' ? toDocParts(version.documentJSON) : null;
  const unreadableVersionError = new Error(
    `Unreadable documentJSON on version "${versionName}" of ${documentId}.`
  );
  const logAndDispatchDownloadError = useLogAndDispatchErrorSnack({
    message: 'Could not read this version, so there is nothing to download.',
  });
  const isJsonDownloadAvailable = useIsJsonDownloadAvailable();
  const wasSentForSignature = getWorkflowWasSentForSig(document?.workflow ?? null);
  const workflow = document?.workflow;
  // Hide templates and standalone docs. Missing variant must not hide a launched instance.
  const isTemplate =
    document?.variant === Apollo.DocumentVariant.Template ||
    workflow?.variant === Apollo.WorkflowVariant.Template;
  const versionSyncCrms = !workflow || isTemplate ? [] : VERSION_SYNC_CRMS;
  const { classes } = useStyles();
  const deleteDisabledReason = getDeleteDisabledReason(canEdit, disableDelete);

  return (
    <>
      <Menu
        disableAutoFocusItem
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setOpen(false)}
        {...rest}
      >
        {!wasSentForSignature && (
          <AlineTooltip arrow placement="left" title={deleteDisabledReason}>
            <span className={classes.menuRow}>
              <MenuItem
                dense
                className={classes.menuItem}
                disabled={!canEdit || disableDelete}
                onClick={async () => {
                  setOpen(false);
                  setDeleteDialogOpen(true);
                }}
              >
                <Trash2 size={16} style={{ marginRight: 8 }} />
                Delete
              </MenuItem>
            </span>
          </AlineTooltip>
        )}
        <span className={classes.menuRow}>
          <MenuItem
            dense
            className={classes.menuItem}
            onClick={async () => {
              setOpen(false);
              if (!isNonSignedAlineDocumentVersion) {
                await downloadFileVersionWithLoading(documentId, { fileVersion: version });
                return;
              }
              if (!docParts) {
                logAndDispatchDownloadError(unreadableVersionError);
                return;
              }
              await initiateDocxExport(documentId, { docParts, name: versionName });
            }}
          >
            <Download size={16} style={{ marginRight: 8 }} />
            Download
          </MenuItem>
        </span>
        {isNonSignedAlineDocumentVersion && isJsonDownloadAvailable && (
          <span className={classes.menuRow}>
            <MenuItem
              dense
              className={classes.menuItem}
              style={{ gap: themeV5.spacing(1) }}
              onClick={async () => {
                setOpen(false);
                if (!docParts) {
                  logAndDispatchDownloadError(unreadableVersionError);
                  return;
                }
                await downloadJSONWithLoading(documentId, { docParts, name: versionName });
              }}
            >
              <FileJson size={16} />
              JSON
              <Chip label="Dev only" size="small" />
            </MenuItem>
          </span>
        )}
        {versionSyncCrms.length > 0 && <Divider className={classes.sectionDivider} />}
        {versionSyncCrms.map(integration => {
          const crm = VERSION_SYNC_CRM[integration];
          const hasLinkedRecord =
            linkedOpportunitiesData?.workflow?.linkedOpportunities.some(
              o => o.integration === integration
            ) ?? false;
          const versionSyncDisabledReason = {
            comingSoon: !crm.enabled && VERSION_SYNC_DISABLED_REASON.comingSoon,
            noEditAccess: !canEdit && VERSION_SYNC_DISABLED_REASON.noEditAccess,
            noLinkedRecord: !hasLinkedRecord && VERSION_SYNC_DISABLED_REASON.noLinkedRecord(crm),
            syncing: syncingToCrm && VERSION_SYNC_DISABLED_REASON.syncing,
          } as const;
          const disabledReason =
            versionSyncDisabledReason.comingSoon ||
            versionSyncDisabledReason.noLinkedRecord ||
            versionSyncDisabledReason.noEditAccess ||
            versionSyncDisabledReason.syncing ||
            '';
          const label = versionSyncDisabledReason.noLinkedRecord || `Sync to ${crm.label}`;
          const tooltip = versionSyncDisabledReason.noLinkedRecord ? '' : disabledReason;
          return (
            <AlineTooltip key={integration} arrow placement="left" title={tooltip}>
              <span className={classes.menuRow}>
                <MenuItem
                  dense
                  className={classes.menuItem}
                  disabled={Boolean(disabledReason)}
                  onClick={() => {
                    setOpen(false);
                    setSyncCrm(integration);
                  }}
                >
                  <CrmIntegrationIcon className={classes.crmIcon} integration={integration} />
                  {label}
                </MenuItem>
              </span>
            </AlineTooltip>
          );
        })}
      </Menu>
      <ConfirmationDialog
        message={`Are you sure you want to delete ${versionName}? This action cannot be undone.`}
        open={deleteDialogOpen}
        title="Delete version?"
        onClickPrimary={async () => {
          const { data } = await deleteFileVersion({
            documentId,
            isDeletingLatestVersion: isLatestVersion,
            options: { variables: { id: version.id } },
          });
          if (data && versionId === version.id) {
            setMonthDay(null);
            goToLatestVersion();
          }
        }}
        onClose={() => setDeleteDialogOpen(false)}
      />
      <ConfirmationDialog
        message={`Are you sure you want to sync ${versionName} to ${syncCrmConfig?.label ?? ''}?`}
        open={Boolean(syncCrm)}
        primaryText="Sync"
        title={syncCrmConfig ? `Sync version to ${syncCrmConfig.label}?` : ''}
        onClickPrimary={async () => {
          if (!syncCrm || !syncCrmConfig) return;
          await syncFileVersionToCrm({
            variables: { input: { fileVersionId: version.id, integration: syncCrm } },
          })
            .then(() =>
              enqueueSnackbar(`Version sync to ${syncCrmConfig.label} started.`, {
                variant: 'success',
              })
            )
            .catch(() =>
              enqueueSnackbar(`Could not sync version to ${syncCrmConfig.label}.`, {
                variant: 'error',
              })
            );
        }}
        onClose={() => setSyncCrm(null)}
      />
    </>
  );
}

function getDeleteDisabledReason(canEdit: boolean, disableDelete: boolean | undefined) {
  if (disableDelete) return 'Cannot delete the only version';
  if (!canEdit) return 'You need edit access to delete';
  return '';
}
